/**
 * Separate auth context for CA (Chartered Accountant) users. Keeps CA subject_type
 * cleanly isolated from dealer/staff/customer auth. Uses the same api client but
 * relies on a different cookie (ca_access_token) set by the backend.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const CaAuthContext = createContext(null);

export function CaAuthProvider({ children }) {
  const [ca, setCa] = useState(null);
  const [pumps, setPumps] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/ca/me");
      setCa(data.ca);
      setPumps(data.pumps || []);
    } catch {
      setCa(null);
      setPumps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/ca/login", { email, password });
    setCa(data.ca);
    await refresh();
    return data.ca;
  };

  const accept = async (token, password) => {
    const { data } = await api.post("/ca/accept", { token, password });
    setCa(data.ca);
    await refresh();
    return data.ca;
  };

  const logout = async () => {
    try { await api.post("/ca/logout"); } catch { /* ignore */ }
    setCa(null);
    setPumps([]);
  };

  return (
    <CaAuthContext.Provider value={{ ca, pumps, loading, refresh, login, accept, logout, formatApiError }}>
      {children}
    </CaAuthContext.Provider>
  );
}

export function useCaAuth() {
  const ctx = useContext(CaAuthContext);
  if (!ctx) throw new Error("useCaAuth must be used inside <CaAuthProvider>");
  return ctx;
}
